import React, { useEffect, useState } from 'react';
import { defaultCountries } from 'react-international-phone';
import type { FieldState } from 'final-form';
import parsePhoneNumber, { CountryCode, getCountries, NumberType } from 'libphonenumber-js/max';
import { get } from 'lodash';
import { Brand } from '@lendi/lala-react';
import getColour from '@lendi-ui/commons/colours';
import Call from '@lendi-ui/icon/Call';

import { PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { InternationalPhoneInput } from '@funnels/ui-components/Common';
import { getBrand, shouldSkipValidate } from '@funnels/utils';

import { Prefix } from '../../_common/';
import { TextInputField, TextInputFieldProps } from '../../Fields';
import UnsubscribedDropdownField from '../../Fields/_uncontrolled/DropdownField';
import { FunnelGrid, FunnelGridUnit } from '../style';

const phoneFormatter = (country: CountryCode) => (number: string | undefined) => {
  if (!number) {
    return '';
  }

  const result = parsePhoneNumber(`${number}`, country);
  if (result?.isValid()) {
    return result.formatNational().replace(/[()]/g, '');
  }

  return number;
};

const phoneParser = (country: CountryCode) => (number: string | undefined) => {
  if (!number) {
    return undefined;
  }

  const sanitisedNumber = `${number}`.replace(/[\D]/g, '');
  const result = parsePhoneNumber(sanitisedNumber, country);
  if (result?.isValid()) {
    return result.number.toString();
  }

  return sanitisedNumber;
};

export const getCountryCodeOptions = () => {
  return getCountries().map((country) => ({ label: country, value: country }));
};

const phoneValidator =
  (
    errorMessage: string,
    phoneTypes: NumberType[],
    isOptional: boolean = false,
    country: CountryCode,
    additionalValidate: (value: any) => any = () => undefined
  ) =>
  (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    try {
      const parsedNumber = parsePhoneNumber(value, country);
      return parsedNumber?.isValid() && parsedNumber.country === country && phoneTypes.includes(parsedNumber.getType())
        ? additionalValidate(value)
        : errorMessage;
    } catch (_e) {
      return errorMessage;
    }
  };

const DEFAULT_PHONE_TYPES: NumberType[] = ['TOLL_FREE', 'SHARED_COST', 'FIXED_LINE', 'MOBILE', 'FIXED_LINE_OR_MOBILE'];
interface PhoneInputProps extends TextInputFieldProps {
  isInternational?: boolean;
  phoneTypes?: NumberType[];
}

export const PhoneInput = (props: PhoneInputProps) => {
  const {
    isInternational = false,
    label = 'Phone number',
    validationFailMessage = 'Please enter a valid phone number.',
    name,
    phoneTypes = DEFAULT_PHONE_TYPES,
    isOptional,
    isDisabled,
    validate: additionalValidate, // only get called after the value passes the default validation
  } = props;

  const brand = getBrand();

  // get initial country when loading the form
  const [formValues, setFormValue] = useFormValues();
  const [country, setCountry] = useState<CountryCode>(isInternational ? 'US' : 'AU');

  useEffect(() => {
    const currentPhoneNumber = get(formValues, name);
    const parsedPhoneNumber = parsePhoneNumber(currentPhoneNumber ?? '');
    if (parsedPhoneNumber?.country && isInternational) {
      setCountry(parsedPhoneNumber.country);
    }
  }, [formValues, isInternational, name]);

  const handleCountryChange = (value: string) => {
    const newCountry = value as CountryCode;
    setFormValue(name, undefined);
    setCountry(newCountry);
  };

  const inputField = (
    <TextInputField
      {...props}
      before={
        <Prefix>
          <Call color={getColour('fill', 'accent')} width="24px" height="24px" />
        </Prefix>
      }
      maxLength={isInternational ? undefined : 12}
      label={label}
      format={phoneFormatter(country)}
      parse={phoneParser(country)}
      validate={phoneValidator(validationFailMessage, phoneTypes, isOptional, country, additionalValidate)}
    />
  );

  if (!isInternational) {
    return inputField;
  }

  if (brand === Brand.Aussie && defaultCountries.length) {
    return (
      <InternationalPhoneInput
        {...props}
        countryList={defaultCountries}
        label="Phone number"
        validationFailMessage="Please enter a valid mobile number."
        showAllowedList
        isFullWidth
        className={PII_FIELD_MASKING_CLASS}
      />
    );
  }

  return (
    <FunnelGrid direction="row">
      <FunnelGridUnit size={{ xs: 1 / 4, xl: 1 / 3 }}>
        <UnsubscribedDropdownField
          label="Country code"
          options={getCountryCodeOptions()}
          size="md"
          isSearchable={true}
          value={country}
          onChange={handleCountryChange}
          isDisabled={isDisabled}
        />
      </FunnelGridUnit>
      <FunnelGridUnit size={{ xs: 3 / 4, xl: 2 / 3 }}>{inputField}</FunnelGridUnit>
    </FunnelGrid>
  );
};
